/* Seoul House — global storefront JS
   Vanilla custom-element pattern (à la Dawn). */

window.SeoulHouse = window.SeoulHouse || {};

(function (SH) {
  const cartEvent = (name, detail) =>
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

  SH.formatMoney = function (cents) {
    const amount = (cents / 100).toLocaleString(document.documentElement.lang || 'ar', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const currency = window.Shopify?.currency?.active || 'SAR';
    return `${amount} ${currency}`;
  };

  SH.escapeHtml = function (value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  };

  const nonStorefrontPath = /^\/(?:admin|cdn|checkout|checkouts|customer_authentication|oauth|payments|services|s\/files|wpm)(?:\/|$)/;

  SH.localizeUrl = function (value) {
    if (!value || value.startsWith('#')) return value;

    let url;
    try {
      url = new URL(value, window.location.href);
    } catch (_) {
      return value;
    }

    const host = url.hostname.replace(/^www\./, '');
    const currentHost = window.location.hostname.replace(/^www\./, '');
    if (!/^https?:$/.test(url.protocol) || host !== currentHost || nonStorefrontPath.test(url.pathname)) {
      return value;
    }

    let path = url.pathname;
    const isEnglish = document.documentElement.lang.toLowerCase().startsWith('en');
    if (isEnglish && path !== '/en' && !path.startsWith('/en/')) {
      path = path === '/' ? '/en' : `/en${path}`;
    } else if (!isEnglish && path === '/en') {
      path = '/';
    } else if (!isEnglish && path.startsWith('/en/')) {
      path = path.slice(3);
    }

    return `${path}${url.search}${url.hash}`;
  };

  SH.localizeNavigation = function (root = document) {
    root.querySelectorAll('a[href]:not([hreflang]):not([data-no-localize])').forEach((link) => {
      link.setAttribute('href', SH.localizeUrl(link.getAttribute('href')));
    });
    root.querySelectorAll('form[method="get"][action]:not([data-no-localize])').forEach((form) => {
      form.setAttribute('action', SH.localizeUrl(form.getAttribute('action')));
    });
  };

  SH.localizeNavigation();
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]:not([hreflang]):not([data-no-localize])');
    if (link) link.setAttribute('href', SH.localizeUrl(link.getAttribute('href')));
  }, true);
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[method="get"][action]:not([data-no-localize])');
    if (form) form.setAttribute('action', SH.localizeUrl(form.getAttribute('action')));
  }, true);

  SH.cart = {
    async add(items) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
        const data = await res.json();
        await SH.cart.refresh();
        cartEvent('cart:item-added', { items: data.items || [data] });
        return data;
      } catch (err) {
        console.error('[SeoulHouse] cart add failed', err);
        cartEvent('cart:error', { message: err.message });
        throw err;
      }
    },
    async change(line, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
        });
        if (!res.ok) throw new Error(`Cart change failed: ${res.status}`);
        await SH.cart.refresh();
      } catch (err) {
        console.error('[SeoulHouse] cart change failed', err);
        cartEvent('cart:error', { message: err.message });
      }
    },
    async refresh() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      cartEvent('cart:updated', { cart });
      return cart;
    },
  };

  /* ---------- GCC shipping destination ---------- */
  const shippingCityValue = (input) => input.value.trim();
  const shippingDestination = (element) => element.closest('[data-shipping-city-selector]');
  // <input list>+<datalist> renders inconsistently on mobile browsers (iOS Safari in
  // particular often shows no suggestion UI at all) — options live in a plain <ul> we
  // render and filter ourselves instead, so it's scrollable and searchable everywhere.
  const shippingCityOptions = (root) => {
    const country = root.querySelector('[data-shipping-country]');
    const source = root.querySelector('[data-shipping-city-options]');
    return Array.from(source.content.querySelectorAll('option'))
      .filter((option) => option.dataset.country === country.value)
      .map((option) => option.value);
  };
  // The <ul> list is suggestions only — any non-empty city the customer types is
  // accepted, since not every delivery city is in the pre-seeded list.
  const shippingCityIsValid = (input) => shippingCityValue(input) !== '';
  const shippingCityShowError = (input, show) => {
    const error = input.closest('[data-shipping-city-selector]')?.querySelector('.shipping-city__error');
    if (error) error.hidden = !show;
    input.classList.toggle('is-invalid', show);
    input.setAttribute('aria-invalid', String(show));
  };
  const shippingDestinationSync = (root) => {
    const city = root.querySelector('[data-shipping-city]');
    const country = root.querySelector('[data-shipping-country]');
    const checkoutCity = root.querySelector('[data-checkout-city]');
    const checkoutCountry = root.querySelector('[data-checkout-country]');
    if (checkoutCity) checkoutCity.value = shippingCityValue(city);
    if (checkoutCountry) checkoutCountry.value = country.value;
  };
  const shippingCityCloseListbox = (root) => {
    const city = root.querySelector('[data-shipping-city]');
    const listbox = root.querySelector('[data-city-listbox]');
    if (!listbox) return;
    listbox.hidden = true;
    city.setAttribute('aria-expanded', 'false');
  };
  const shippingCityOpenListbox = (root) => {
    const city = root.querySelector('[data-shipping-city]');
    const listbox = root.querySelector('[data-city-listbox]');
    if (!listbox) return;
    const filter = shippingCityValue(city).toLocaleLowerCase();
    const matches = shippingCityOptions(root).filter(
      (name) => !filter || name.toLocaleLowerCase().includes(filter)
    );
    listbox.replaceChildren(...matches.map((name) => {
      const li = document.createElement('li');
      li.textContent = name;
      li.setAttribute('role', 'option');
      li.dataset.value = name;
      return li;
    }));
    listbox.hidden = matches.length === 0;
    city.setAttribute('aria-expanded', String(matches.length > 0));
  };
  const shippingDestinationSetCities = (root, clearCity = false) => {
    const city = root.querySelector('[data-shipping-city]');
    if (clearCity || !shippingCityIsValid(city)) city.value = '';
    shippingDestinationSync(root);
  };
  const shippingDestinationSave = async (root) => {
    const city = root.querySelector('[data-shipping-city]');
    const country = root.querySelector('[data-shipping-country]');
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { 'دولة الشحن': country.value, 'مدينة الشحن': shippingCityValue(city) },
      }),
    });
  };

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-shipping-city]');
    if (!input) return;
    shippingCityShowError(input, false);
    const root = shippingDestination(input);
    shippingDestinationSync(root);
    shippingCityOpenListbox(root);
  });

  document.addEventListener('focusin', (event) => {
    const input = event.target.closest('[data-shipping-city]');
    if (!input) return;
    shippingCityOpenListbox(shippingDestination(input));
  });

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-city-listbox] li');
    if (option) {
      const root = shippingDestination(option);
      const city = root.querySelector('[data-shipping-city]');
      city.value = option.dataset.value;
      city.dispatchEvent(new Event('input', { bubbles: true }));
      city.dispatchEvent(new Event('change', { bubbles: true }));
      shippingCityCloseListbox(root);
      return;
    }
    document.querySelectorAll('[data-city-combobox]').forEach((combobox) => {
      if (!combobox.contains(event.target)) shippingCityCloseListbox(shippingDestination(combobox));
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const input = event.target.closest('[data-shipping-city]');
    if (!input) return;
    shippingCityCloseListbox(shippingDestination(input));
  });

  document.addEventListener('change', async (event) => {
    const country = event.target.closest('[data-shipping-country]');
    if (country) {
      const root = shippingDestination(country);
      const city = root.querySelector('[data-shipping-city]');
      shippingDestinationSetCities(root, true);
      shippingCityShowError(city, false);
      try { await shippingDestinationSave(root); } catch (_) {}
      return;
    }

    const input = event.target.closest('[data-shipping-city]');
    if (!input) return;
    const valid = shippingCityIsValid(input);
    shippingCityShowError(input, !valid);
    if (!valid) return;

    const root = shippingDestination(input);
    shippingDestinationSync(root);
    try { await shippingDestinationSave(root); } catch (_) {
      /* The cart form still saves the city when checkout is submitted. */
    }
  });

  document.querySelectorAll('[data-shipping-city-selector]').forEach((root) => {
    shippingDestinationSetCities(root);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form');
    const root = form?.querySelector('[data-shipping-city-selector]');
    const input = root?.querySelector('[data-shipping-city]');
    const country = root?.querySelector('[data-shipping-country]');
    if (!input || (event.submitter && event.submitter.name !== 'checkout')) return;
    if (shippingCityIsValid(input)) {
      shippingDestinationSync(root);
      shippingCityShowError(input, false);
      const action = new URL(form.action, window.location.href);
      action.searchParams.set('checkout[shipping_address][city]', shippingCityValue(input));
      action.searchParams.set('checkout[shipping_address][country]', country.value);
      form.action = action.toString();
      return;
    }
    event.preventDefault();
    shippingCityShowError(input, true);
    input.scrollIntoView({ block: 'center', behavior: 'smooth' });
    input.focus({ preventScroll: true });
  });

  /* ---------- Mega menu ----------
     <nav data-mega-nav>
       <button data-mega-trigger="skincare">…</button>
       <div data-mega-panel="skincare" aria-hidden="true">…</div>
     </nav>
  */
  class MegaNav extends HTMLElement {
    connectedCallback() {
      this.triggers = this.querySelectorAll('[data-mega-trigger]');
      this.panels = this.querySelectorAll('[data-mega-panel]');
      this.activeId = null;
      this.triggers.forEach((btn) => {
        const id = btn.getAttribute('data-mega-trigger');
        btn.addEventListener('mouseenter', () => this.open(id));
        btn.addEventListener('focus', () => this.open(id));
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.activeId === id ? this.close() : this.open(id);
        });
      });
      this.panels.forEach((panel) => {
        panel.addEventListener('mouseenter', () =>
          this.open(panel.getAttribute('data-mega-panel'))
        );
      });
      this.addEventListener('mouseleave', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open(id) {
      this.activeId = id;
      this.panels.forEach((p) => {
        p.setAttribute('aria-hidden', p.getAttribute('data-mega-panel') === id ? 'false' : 'true');
      });
      this.triggers.forEach((t) => {
        t.setAttribute('aria-expanded', t.getAttribute('data-mega-trigger') === id ? 'true' : 'false');
      });
    }
    close() {
      this.activeId = null;
      this.panels.forEach((p) => p.setAttribute('aria-hidden', 'true'));
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    }
  }
  customElements.define('mega-nav', MegaNav);

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.backdrop = this.querySelector('[data-cart-backdrop]');
      this.closeBtn = this.querySelector('[data-cart-close]');
      this.lastFocus = null;

      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:item-added', () => this.open());
      document.addEventListener('cart:updated', (e) => this.render(e.detail.cart));
      this.backdrop.addEventListener('click', () => this.close());
      this.closeBtn.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') this.close();
      });

      this.addEventListener('click', (e) => {
        const inc = e.target.closest('[data-qty-inc]');
        const dec = e.target.closest('[data-qty-dec]');
        const remove = e.target.closest('[data-qty-remove]');
        if (inc) return this._delta(inc, +1);
        if (dec) return this._delta(dec, -1);
        if (remove) return this._delta(remove, 'remove');
        const addBtn = e.target.closest('[data-add-suggestion]');
        if (addBtn) {
          const id = addBtn.getAttribute('data-variant-id');
          const localizedTitle = addBtn.getAttribute('data-localized-title');
          if (id) {
            const item = { id, quantity: 1 };
            if (localizedTitle) item.properties = { _localized_title: localizedTitle };
            SH.cart.add([item]);
          }
        }
      });
    }
    _delta(el, op) {
      const line = parseInt(el.getAttribute('data-line'), 10);
      const current = parseInt(el.getAttribute('data-qty'), 10) || 0;
      const next = op === 'remove' ? 0 : current + op;
      SH.cart.change(line, Math.max(0, next));
    }
    open() {
      this.lastFocus = document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.closeBtn.focus();
    }
    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus();
    }
    render(cart) {
      const countEls = document.querySelectorAll('[data-cart-count]');
      countEls.forEach((el) => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
      const subtotalEl = this.querySelector('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = SH.formatMoney(cart.total_price);
      const lines = this.querySelector('[data-cart-lines]');
      const empty = this.querySelector('[data-cart-empty]');
      if (!cart.items.length) {
        if (lines) lines.hidden = true;
        if (empty) empty.hidden = false;
        return;
      }
      if (empty) empty.hidden = true;
      if (lines) {
        const localizedTitles = new Map(
          Array.from(lines.querySelectorAll('[data-cart-key][data-localized-title]')).map((line) => [
            line.getAttribute('data-cart-key'),
            line.getAttribute('data-localized-title'),
          ])
        );
        lines.hidden = false;
        lines.innerHTML = cart.items
          .map((it, i) => {
            const localizedTitle = it.properties?._localized_title || localizedTitles.get(it.key);
            const itemTitle = document.documentElement.lang.startsWith('ar') && localizedTitle
              ? localizedTitle
              : it.product_title;
            const safeTitle = SH.escapeHtml(itemTitle);
            return `
          <div class="cart-line" data-cart-key="${SH.escapeHtml(it.key)}" data-localized-title="${safeTitle}">
            <div class="cart-line__media"><img src="${SH.escapeHtml(it.image || '')}" alt="${safeTitle}" loading="lazy" /></div>
            <div>
              <div class="cart-line__title">${safeTitle}</div>
              <div class="cart-line__subtitle">${SH.escapeHtml(it.variant_title || '')}</div>
              <div class="cart-line__qty">
                <button type="button" data-qty-dec data-line="${i + 1}" data-qty="${it.quantity}" aria-label="نقص">−</button>
                <input type="text" value="${it.quantity}" readonly aria-label="الكمية" />
                <button type="button" data-qty-inc data-line="${i + 1}" data-qty="${it.quantity}" aria-label="زيادة">+</button>
              </div>
            </div>
            <div class="cart-line__price">${SH.formatMoney(it.final_line_price)}</div>
          </div>`;
          })
          .join('');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Cart toggle (header icon) ---------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-trigger]');
    if (trigger) {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  });

  /* ---------- Mobile nav drawer ----------
     Delegated so it works regardless of script/DOM load order. */
  const closeMobileNav = () => {
    const drawer = document.querySelector('[data-mobile-nav]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.querySelectorAll('[data-mobile-nav-open]').forEach((b) =>
      b.setAttribute('aria-expanded', 'false')
    );
  };
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-mobile-nav-open]');
    if (openBtn) {
      e.preventDefault();
      const drawer = document.querySelector('[data-mobile-nav]');
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      openBtn.setAttribute('aria-expanded', 'true');
      const closeBtn = drawer.querySelector('.mobile-nav__close');
      if (closeBtn) closeBtn.focus();
      return;
    }
    if (e.target.closest('[data-mobile-nav-close]')) {
      closeMobileNav();
      return;
    }
    const acc = e.target.closest('[data-mobile-acc]');
    if (acc) {
      const expanded = acc.getAttribute('aria-expanded') === 'true';
      acc.setAttribute('aria-expanded', String(!expanded));
      const sub = acc.nextElementSibling;
      if (sub) sub.hidden = expanded;
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMobileNav();
  });

  /* ---------- Product carousel ---------- */
  class ProductScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroller-track]');
      if (!this.track) return;
      const prev = this.querySelector('[data-scroller-prev]');
      const next = this.querySelector('[data-scroller-next]');
      const step = () => this.track.clientWidth * 0.8;
      // Infinite loop: wrap to the other end at the edges. Works in RTL and LTR
      // (modern browsers use a negative scrollLeft in RTL, so map an absolute
      // magnitude 0..max onto the signed scrollLeft with `dir`).
      const rtl = () => getComputedStyle(this.track).direction === 'rtl';
      const max = () => this.track.scrollWidth - this.track.clientWidth;
      const go = (forward) => {
        const dir = rtl() ? -1 : 1;
        const m = max();
        const cur = Math.abs(this.track.scrollLeft);
        let target;
        if (forward) {
          target = cur >= m - 5 ? 0 : Math.min(cur + step(), m);
        } else {
          target = cur <= 5 ? m : Math.max(cur - step(), 0);
        }
        this.track.scrollTo({ left: dir * target, behavior: 'smooth' });
      };
      prev?.addEventListener('click', () => go(false));
      next?.addEventListener('click', () => go(true));
    }
  }
  customElements.define('product-scroller', ProductScroller);

  /* ---------- Add-to-cart forms ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-add-to-cart]');
    if (!form) return;
    e.preventDefault();
    const formData = new FormData(form);
    const id = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);
    const localizedTitle = formData.get('properties[_localized_title]');
    if (!id) return;
    const item = { id, quantity };
    if (localizedTitle) item.properties = { _localized_title: localizedTitle };
    await SH.cart.add([item]);
  });

  /* ---------- On load: hydrate cart ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    SH.cart.refresh().catch(() => {});
  });
})(window.SeoulHouse);
